import { useEffect, useMemo, useState } from 'react';
import { LayoutElement } from '../types';
import { getFactory3DStyle } from '../utils/factory3dStyles';
import { getElement3DExtrusionDepth } from '../utils/layout3d';
import { isElementVisible, isLayerHidden } from '../utils/layoutImport';
import { CompactInstanceElement, InstanceBatchResult } from '../workers/scene3dCompute';
import { scene3dWorkerClient } from '../workers/scene3dWorkerClient';
import { isFloor3DElement } from '../utils/layout3dFloor';

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function serializeElements(
  elements: Record<string, LayoutElement>,
  elementOrder: string[],
  hiddenLayerIds: Set<string>
): CompactInstanceElement[] {
  const items: CompactInstanceElement[] = [];

  for (const id of elementOrder) {
    const element = elements[id];
    if (!element || !isElementVisible(element)) continue;
    if (isLayerHidden(element, hiddenLayerIds)) continue;
    if (!isFloor3DElement(element)) continue;

    const { position, rotation, dimensions, metadata, type } = element;
    const style = getFactory3DStyle(type);

    items.push({
      type,
      x: position.x,
      y: position.y,
      z: position.z ?? 0,
      rot: rotation,
      w: Math.max(1, dimensions.width),
      h: Math.max(1, dimensions.height),
      d: Math.max(2, getElement3DExtrusionDepth(element)),
      color: metadata.color ?? style.color,
      railType: readString(element.customProperties?.railType),
    });
  }

  return items;
}

export function use3dInstanceBatches(
  elements: Record<string, LayoutElement>,
  elementOrder: string[],
  hiddenLayerIds: Set<string>,
  enabled: boolean
): { batches: InstanceBatchResult[]; computing: boolean } {
  const compactItems = useMemo(
    () => (enabled ? serializeElements(elements, elementOrder, hiddenLayerIds) : []),
    [elements, elementOrder, hiddenLayerIds, enabled]
  );

  const [batches, setBatches] = useState<InstanceBatchResult[]>([]);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    if (!enabled || compactItems.length === 0) {
      setBatches([]);
      setComputing(false);
      return;
    }

    let cancelled = false;
    setComputing(true);

    scene3dWorkerClient
      .computeInstanceBatches(compactItems, true)
      .then((result) => {
        if (!cancelled) {
          setBatches(result);
          setComputing(false);
        }
      })
      .catch(() => {
        if (!cancelled) setComputing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [compactItems, enabled]);

  return { batches, computing };
}
